import eventBus from '~/routes/_shared/data/hooks/useEventBus/eventBus/eventBus';

type Transform = <T extends Record<string, unknown>>(payload: T) => unknown;

interface EventMappingConfig<
  ExternalEventsType extends string = string,
  EventsType extends string = string,
> {
  from: ExternalEventsType;
  to: EventsType;
  transform?: Transform;
}

export interface EventEmitter {
  emit(eventType: string, payload?: unknown): void;
}

export interface ExternalEvent {
  type: string;
  payload?: Record<string, unknown>;
}

export interface ExternalEventSource {
  subscribe(callback: (message: ExternalEvent) => void): void;

  unsubscribe(): void;
}

class EventMapping {
  readonly from: string;
  readonly to: string;
  readonly transform: Transform;

  constructor(config: EventMappingConfig) {
    this.from = config.from;
    this.to = config.to;
    this.transform = config.transform || ((data) => data);
  }

  matches(externalEvent: ExternalEvent): boolean {
    return this.from === externalEvent.type;
  }
}

class EventGateway {
  private mappings: EventMapping[] = [];
  private emitter: EventEmitter;
  private eventSources: ExternalEventSource[] = [];

  constructor(emitter: EventEmitter) {
    this.emitter = emitter;
  }

  private findMappingsFor(externalEvent: ExternalEvent): EventMapping[] {
    return this.mappings.filter((mapping) => mapping.matches(externalEvent));
  }

  addEventSource(eventSource: ExternalEventSource) {
    this.eventSources.push(eventSource);

    return () => {
      const index = this.eventSources.indexOf(eventSource);
      if (index !== -1) {
        this.eventSources[index].unsubscribe();
        this.eventSources.splice(index, 1);
      }
    };
  }

  registerMappings<ExternalEventsType extends string, EventsType extends string>(
    configs: EventMappingConfig<ExternalEventsType, EventsType>[]
  ) {
    const mappings = configs.map((config) => new EventMapping(config));
    this.mappings.push(...mappings);
  }

  receive(externalEvent: ExternalEvent) {
    const mappings = this.findMappingsFor(externalEvent);
    mappings.forEach((mapping) => {
      if (externalEvent.payload) {
        this.emitter.emit(mapping.to, mapping.transform(externalEvent.payload));
      } else {
        this.emitter.emit(mapping.to);
      }
    });
  }

  start() {
    this.eventSources.forEach((source) => {
      source.subscribe((event: ExternalEvent) => {
        this.receive(event);
      });
    });
  }
}

export default new EventGateway(eventBus);
export { EventGateway };
